import { create } from 'zustand';
import { Locale, translations, TranslationKey } from './translations';

const STORAGE_KEY = 'gamehub-locale';

function getInitialLocale(): Locale {
	if (typeof window === 'undefined') return 'en';
	try {
		const saved = localStorage.getItem(STORAGE_KEY);
		if (saved === 'en' || saved === 'sv') return saved;
	} catch {
		/* ignore */
	}
	const nav = navigator.language?.toLowerCase() ?? '';
	if (nav.startsWith('sv')) return 'sv';
	return 'en';
}

interface LanguageStore {
	locale: Locale;
	setLocale: (locale: Locale) => void;
	toggleLocale: () => void;
}

const useLanguageStore = create<LanguageStore>((set, get) => ({
	locale: getInitialLocale(),
	setLocale: (locale) => {
		try {
			localStorage.setItem(STORAGE_KEY, locale);
		} catch {
			/* ignore */
		}
		set({ locale });
	},
	toggleLocale: () => {
		const next: Locale = get().locale === 'en' ? 'sv' : 'en';
		try {
			localStorage.setItem(STORAGE_KEY, next);
		} catch {
			/* ignore */
		}
		set({ locale: next });
	},
}));

/** Subscribe to locale so components re-render when language changes */
export function useT() {
	const locale = useLanguageStore((s) => s.locale);
	return (key: TranslationKey): string =>
		translations[locale][key] ?? translations.en[key] ?? key;
}

export function useLocale() {
	return useLanguageStore((s) => s.locale);
}

export function useToggleLocale() {
	return useLanguageStore((s) => s.toggleLocale);
}

/** @deprecated prefer useT / useLocale / useToggleLocale */
const useLanguage = Object.assign(
	(selector?: (s: LanguageStore & { t: (k: TranslationKey) => string }) => unknown) => {
		const locale = useLanguageStore((s) => s.locale);
		const setLocale = useLanguageStore((s) => s.setLocale);
		const toggleLocale = useLanguageStore((s) => s.toggleLocale);
		const t = (key: TranslationKey) =>
			translations[locale][key] ?? translations.en[key] ?? key;
		const state = { locale, setLocale, toggleLocale, t };
		return selector ? selector(state) : state;
	},
	{ getState: useLanguageStore.getState }
);

export default useLanguage;
